import styles from "./HeaderComponentV2.module.scss";
import UnitedIcon from "../../assets/icons/United.svg";
import {
  Avatar,
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useGetGlobalMenuQuery } from "./services/GetGlobalMenuAPISlice";
import appLogo from "../../assets/images/app-logo/app_logo.png";

import {
  getNameInitials,
  getPatientRoute,
  routeToGlobalMenuId,
  tabHeaderBgColorMap,
} from "../../utility/functions/common-functions";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useGetPatientProfileQuery } from "./services/GetPatientProfileAPISlice";
import { patientProfileDetailsActions } from "./services/PatientProfileDetailsSlice";
import { patientMasterDataActions } from "./services/PatientMasterDataSlice";
import { usePreferredLangMutation } from "./SavePreferredLangAPISlice";
import CustomSpinner from "../../components/custom-spinner/CustomSpinner";
import { enqueueSnackbar } from "notistack";
import { useGetPatientActiveLanguageQuery } from "./GetPatientActiveLanguageSlice";
import { ActiveLanguages } from "../../utility/interfaces/patientMasterDataInterface";
import { useGetUserPreferenceQuery } from "./services/GetUserPrefernceApiSlice";
import { setPatientPreferences } from "./services/PatientUserPreferenceDataSlice";
import CustomTab from "../../components/v2/custom-tab/CustomTab";
import AvatarMenu from "./AvatarMenu";
import MobileDrawer from "./MobileDrawer";
import { patientAuthSliceActions } from "../../auth/patient-auth/PatientAuthSlice";
import { useLocation } from "react-router-dom";
import { useNavigationBlock } from "../../routes/PatientProtectedRoute";
import { resetExpandedGroups } from "../../pages/V2Pages/my-care-plan-page/services/carePlanUISlice";
import { resetFhirAuthContext } from "../../auth/patient-auth/FhirAuthProvider";
import { resetLearningContent } from "../view-learning-content/services/LearningContentDataSlice";
import {
  trackCurrentPageDuration,
  trackEvent,
} from "../../app-insights/AppInsights";

const HeaderComponent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // md = 900px
  const hasAccess = useAppSelector(
    (state) => state.patientOnboardingStatus.isOptedIn
  );
  const [selectedTab, setSelectedTab] = useState(0);
  const location = useLocation();
  const isProfilePage = location.pathname.includes("/profile");
  const initialTabId = location.state?.selectedTabId;

  const pathToTabIdMap: { [key: string]: number } = {
    "/care-plan": 2,
    "/ultrasound": 3,
    "/my-reward-activity": 4,
    "/learning": 5,
  };

  useEffect(() => {
    if (initialTabId) {
      setSelectedTab(initialTabId);
    } else {
      const matchedPath = Object.keys(pathToTabIdMap).find((path) =>
        location.pathname.includes(path)
      );

      if (matchedPath) {
        setSelectedTab(pathToTabIdMap[matchedPath]);
      } else if (!location.pathname.includes("/profile")) {
        setSelectedTab(1); // Default to Dashboard if not Profile page
      }
    }
  }, [initialTabId, location.pathname]);

  const headerBgColor = isProfilePage
    ? tabHeaderBgColorMap["profile"]
    : tabHeaderBgColorMap[selectedTab] || "#ffffff";

  const patientProfileInfo = useAppSelector(
    (state) => state.patientProfileDetails.patientProfileInfo
  );

  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );
  const userName = useAppSelector(
    (state) => state.patientProfileDetails.patientProfileInfo.name
  );

  const { i18n, t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const [selectedLanguage, setSelectedLanguage] = useState(
    patientPreferredLang.toUpperCase()
  );

  localStorage.setItem("patientPreferredLang", patientPreferredLang);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [PreferredLangSave] = usePreferredLangMutation();

  const { data: patientProfileApiData, isLoading: patientProfileApiLoading } =
    useGetPatientProfileQuery(
      {
        patientId: patientProfileInfo.patientProfileId,
        preferredLanguage: patientPreferredLang,
      },
      { skip: !hasAccess }
    );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuAnchorEl(null);
  };

  useEffect(() => {
    handleClose();
  }, [isMobile]);

  const onChangeLang = async (selectedLang: ActiveLanguages) => {
    if (
      selectedLang.languageName.toLowerCase() !== selectedLanguage.toLowerCase()
    ) {
      try {
        i18n.changeLanguage(selectedLang.languageCode);
        setSelectedLanguage(selectedLang.languageName); // Update the selected language
        setAnchorEl(null);
        const body = {
          patientProfileId: patientProfileInfo.patientProfileId,
          prefferedLanguageId: selectedLang.languageId,
          userName: patientProfileInfo.userName,
        };

        await PreferredLangSave({ body }).unwrap();
      } catch (err: any) {
        enqueueSnackbar(t("anErrorOccurred"), { variant: "error" });
      } finally {
        window.location.reload();
      }
    }
  };

  const handleProfileClick = () => {
    dispatch(resetExpandedGroups());
    setMenuAnchorEl(null);
    navigate(getPatientRoute(patientProfileInfo.patientProfileId, "/profile"));
    setSelectedTab(0);
  };

  const {
    data,
    isLoading: globalMenuApiLoading,
    isError: isGlobalMenuError,
  } = useGetGlobalMenuQuery({ patientPreferredLang });

  const { data: getPatientActiveLanguageData } =
    useGetPatientActiveLanguageQuery({});

  const { data: getUserPreferenceData } = useGetUserPreferenceQuery(
    patientProfileInfo.patientProfileId
  );

  const handleLogOutClick = () => {
    trackCurrentPageDuration(userName);
    trackEvent("PatientLogout", { userName });
    dispatch(patientAuthSliceActions.clearUser());
    resetFhirAuthContext();
  };

  useEffect(() => {
    if (patientProfileApiData) {
      dispatch(
        patientProfileDetailsActions.setPatientProfileDetails(
          patientProfileApiData
        )
      );

      setSelectedLanguage(
        patientProfileApiData?.patientProfileInfo?.preferredLanguage
      );

      i18n.changeLanguage(patientPreferredLang);
    }
    if (getPatientActiveLanguageData) {
      dispatch(
        patientMasterDataActions.setActiveLanguages(
          getPatientActiveLanguageData
        )
      ); // Dispatching the fetched data to the store

      const lang = getPatientActiveLanguageData?.supportedLanguages.filter(
        (row: ActiveLanguages) => {
          return row.languageCode === patientPreferredLang;
        }
      );

      setSelectedLanguage(lang[0]?.languageName);
    }
    if (getUserPreferenceData) {
      dispatch(
        setPatientPreferences(getUserPreferenceData.patientProfilePreferences)
      );
    }
  }, [
    patientProfileApiData,
    getPatientActiveLanguageData,
    patientPreferredLang,
    getUserPreferenceData,
    dispatch,
    i18n,
  ]);

  const { requestNavigation, hasChanges } = useNavigationBlock();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = data.globalMenus.find(
      (tab: { globalMenuId: number }) => tab.globalMenuId === newValue
    );

    if (!selectedTab) return;

    const routeFn = routeToGlobalMenuId[selectedTab.globalMenuId];

    const proceedNavigation = () => {
      setSelectedTab(newValue);
      dispatch(resetExpandedGroups());
      dispatch(resetLearningContent());
      if (routeFn) {
        navigate(routeFn(patientProfileInfo.patientProfileId), {
          state: { selectedTabId: newValue },
        });
      }
    };

    if (hasChanges) {
      requestNavigation(proceedNavigation);
    } else {
      proceedNavigation();
    }
  };

  const handleLogoClick = (id: number) => {
    dispatch(resetExpandedGroups());
    const routeFn = routeToGlobalMenuId[id];
    requestNavigation(() =>
      navigate(routeFn(patientProfileInfo.patientProfileId), {
        state: { selectedTabId: id },
      })
    );
  };
  const isLoading = patientProfileApiLoading || globalMenuApiLoading;

  if (isLoading) {
    return <CustomSpinner />;
  }
  return (
    <>
      {isMobile ? (
        <Box className={styles.mobileHeader}>
          <img
            src={appLogo}
            alt="AppLogo"
            width="120px"
            height="70px"
            className={styles.mobileAppLogo}
            onClick={() => handleLogoClick(1)}
          />

          <IconButton
            onClick={() => setDrawerOpen(true)}
            className={styles.menuIcon}
            sx={{
              color: "inherit",
            }}
          >
            <MenuIcon />
          </IconButton>

          {!isGlobalMenuError && (
            <MobileDrawer
              open={drawerOpen}
              userName={
                patientProfileInfo.userName
                  ? patientProfileInfo.userName
                  : patientProfileInfo.name?.split(" ")[0] || ""
              }
              onClose={() => {
                setDrawerOpen(false);
              }}
              onProfileClick={handleProfileClick}
              supportedLanguages={
                getPatientActiveLanguageData?.supportedLanguages || []
              }
              selectedLanguage={selectedLanguage}
              langAnchorEl={anchorEl}
              onLangMenuOpen={(e) => setAnchorEl(e.currentTarget)}
              onLangMenuClose={() => setAnchorEl(null)}
              onLangChange={onChangeLang}
              tabs={data?.globalMenus}
              onTabClick={handleTabChange}
              onLogoutClick={handleLogOutClick}
            />
          )}
        </Box>
      ) : (
        <Box className={styles.headerContainer}>
          <Box className={styles.innerHeader}>
            <Box className={styles.leftSection}>
              <img
                src={appLogo}
                alt="appLogo"
                width="120px"
                height="70px"
                onClick={() => handleLogoClick(1)}
                className={styles.appLogo}
              />
              {!isGlobalMenuError && (
                <Box>
                  <CustomTab
                    tabs={data?.globalMenus}
                    value={selectedTab}
                    handleTabChange={handleTabChange}
                  />
                </Box>
              )}
            </Box>

            {hasAccess && (
              <Box className={styles.rightSection}>
                <IconButton onClick={handleMenuClick}>
                  <Avatar className={styles.avatar}>
                    <Typography variant="h5">
                      {getNameInitials(patientProfileInfo.name)}
                    </Typography>
                  </Avatar>
                </IconButton>

                <AvatarMenu
                  anchorEl={menuAnchorEl}
                  open={menuOpen}
                  onClose={handleClose}
                  onProfileClick={handleProfileClick}
                  onLanguageClick={handleClick}
                  selectedLanguage={selectedLanguage}
                  avatarLabel={patientProfileInfo.name}
                  unitedIcon={UnitedIcon}
                  supportedLanguages={
                    getPatientActiveLanguageData?.supportedLanguages || []
                  }
                  onChangeLang={onChangeLang}
                  onLogoutClick={handleLogOutClick}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default HeaderComponent;
